import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import ImgLogo from "../../images/logo.png";

import { classNames } from "primereact/utils";
import { PanelMenu } from "primereact/panelmenu";
import { TieredMenu } from "primereact/tieredmenu";

function renderTieredItem(module, item) {
  return (
    <button
      type="button"
      className={classNames(
        "p-menuitem-link white-space-nowrap line-height-1 border-none bg-transparent w-full",
        {
          "flex flex-column align-items-center": module === "main",
        },
      )}
    >
      <span className={item.icon} />
      <span
        className={classNames({
          "text-xs mt-1": module === "main",
        })}
      >
        {item[module === "main" ? "label_sm" : "label"]}
      </span>
    </button>
  );
}

function renderExpand(item, options, layer = 0) {
  return (
    <button
      type="button"
      className={classNames(options.className, "border-none w-full")}
      style={{ paddingLeft: 1 + layer * 1.13 + "rem" }}
      onClick={options.onClick}
    >
      {options.iconClassName !== "p-menuitem-icon" && (
        <span className={classNames(options.iconClassName)}></span>
      )}
      <span className={classNames(options.labelClassName, "mr-auto")}>
        {item.label}
      </span>
      <i
        className={`pi pi-angle-down -rotate-${
          options.active ? "180" : "0"
        } transition-all transition-duration-200 transition-ease-in-out`}
      ></i>
    </button>
  );
}

function stopToggleTransition() {
  let target = document.getElementsByClassName("p-toggleable-content");
  if (target[0]) target[0].style.transition = "none";
}

function renderLink(item, options, layer) {
  return (
    <button
      type="button"
      className={classNames(options.className, "border-none w-full")}
      style={{ paddingLeft: 1 + layer * 1.13 + "rem" }}
      onClick={stopToggleTransition}
    >
      {item.icon && <span className={classNames(options.iconClassName)}></span>}
      <span className={classNames(options.labelClassName, "mr-auto")}>
        {item.label}
      </span>
    </button>
  );
}

function matchExpand({ pathname, path }) {
  return pathname.includes(path);
}

function matchPath({ pathname, path }) {
  return pathname.includes(path) ? "menu-active" : "";
}

function getRouteTemplate({ module, type, tieredType, layer = 0 }) {
  if (module !== "panel") {
    return (item) => renderTieredItem(tieredType, item);
  }

  if (type === "expand") {
    return (item, options) => renderExpand(item, options, layer);
  }

  return (item, options) => renderLink(item, options, layer);
}

function formatChildRoutes({ childs, layer, module, pathname, navigate }) {
  const childLayer = layer + 1;

  return childs.map((child) => {
    if (child.items && child.items.length > 0) {
      return {
        label: child.label,
        icon: child.icon,
        expanded: matchExpand({ pathname, path: child.path }),
        template: getRouteTemplate({
          module,
          type: "expand",
          tieredType: "child",
          layer: childLayer,
        }),
        items: formatChildRoutes({
          childs: child.items,
          layer: childLayer,
          module,
          pathname,
          navigate,
        }),
      };
    }

    return {
      label: child.label,
      icon: child.icon,
      className: matchPath({ pathname, path: child.path }),
      template: getRouteTemplate({
        module,
        type: "link",
        tieredType: "child",
        layer: childLayer,
      }),
      command: () => {
        navigate(child.path);
      },
    };
  });
}

function getRouteIcon({ module, icon }) {
  if (module === "tiered" && !icon) return "pi pi-file";
  return icon;
}

function formatRoutes({ module, rawData, pathname, navigate }) {
  return rawData.map((route) => {
    const routeIcon = getRouteIcon({ module, icon: route.icon });

    if (route.items && route.items.length > 0) {
      return {
        label: route.label,
        label_sm: route.label_sm ?? route.label,
        icon: routeIcon,
        expanded: matchExpand({ pathname, path: route.path }),
        template: getRouteTemplate({
          module,
          type: "expand",
          tieredType: "main",
        }),
        items: formatChildRoutes({
          childs: route.items,
          layer: 0,
          module,
          pathname,
          navigate,
        }),
      };
    }

    return {
      label: route.label,
      label_sm: route.label_sm ?? route.label,
      icon: routeIcon,
      className: matchPath({ pathname, path: route.path }),
      template: getRouteTemplate({
        module,
        type: "link",
        tieredType: "main",
        layer: 0,
      }),
      command: () => {
        navigate(route.path);
      },
    };
  });
}

export default function BackSidebar(props) {
  let { LayoutCss } = props;
  const route_location = useLocation();
  const route_navigate = useNavigate();
  const redux_system = useSelector((state) => state.system);
  const [menuItemTiered, setMenuItemTiered] = useState([]);
  const [menuItemPanel, setMenuItemPanel] = useState([]);

  useEffect(() => {
    let routes = [
      {
        label: "總覽",
        path: "/home",
        icon: "pi pi-home",
        tooltip: "ASD",
      },
      {
        label: "能源監測",
        path: "/energyDetect",
        icon: "pi pi-pen-to-square",
        items: [
          {
            label: "峰平谷統計",
            path: "/energyDetect/updownStatistic",
          },
          {
            label: "公區電錶數據",
            path: "/energyDetect/electricityMeterTable",
          },
          {
            label: "公區電錶儀表板",
            path: "/energyDetect/electricityMeterBoard",
          },
        ],
      },
      {
        label: "耗能分析",
        path: "/energyUse",
        icon: "pi pi-chart-bar",
        items: [
          {
            label: "需量管理",
            path: "/energyUse/demandManage",
          },
          {
            label: "最佳契約容量",
            path: "/energyUse/optimalContractCapacity",
          },
          {
            label: "多對象對比",
            path: "/energyUse/multiTargetCompare",
          },
          {
            label: "多時間對比",
            path: "/energyUse/multiTimeCompare",
          },
          {
            label: "能耗統計報表",
            path: "/energyUse/usageStatistic",
          },
          {
            label: "能耗費用統計",
            path: "/energyUse/feeStatistic",
          },
          {
            label: "樓群能耗比較",
            path: "/energyUse/buildingCompare",
          },
          {
            label: "區域績效",
            path: "/energyUse/areaPerformance",
          },
          {
            label: "能耗同環比",
            path: "/energyUse/usageMQoMQ",
          },
          {
            label: "綠能同環比",
            path: "/energyUse/greenMQoMQ",
          },
        ],
      },
      {
        label: "HEMS",
        path: "/hems",
        icon: "pi pi-users",
      },
      {
        label: "設定",
        path: "/setting",
        icon: "pi pi-cog",
        items: [
          {
            label: "電力電價",
            path: "/setting/electricityPrice",
          },
          {
            label: "目標需量",
            path: "/setting/controlDemand",
          },
        ],
      },
    ];

    setMenuItemTiered(
      formatRoutes({
        module: "tiered",
        rawData: routes,
        pathname: route_location.pathname,
        navigate: route_navigate,
      }),
    );
    setMenuItemPanel(
      formatRoutes({
        module: "panel",
        rawData: routes,
        pathname: route_location.pathname,
        navigate: route_navigate,
      }),
    );
  }, [route_location.pathname, route_navigate]);

  return (
    <div
      className={classNames(LayoutCss["layout-sidebar"], {
        [LayoutCss["layout-sidebar-shrink"]]: redux_system.backLayout.isShrink,
      })}
    >
      <div
        className={classNames(
          "h-full border-right-2 border-dark-d py-3",
          LayoutCss["layout-sidebar-inner"],
        )}
      >
        <div
          className={classNames(LayoutCss["layout-logo"], {
            "fadeinleft animation-ease-in animation-duration-200":
              !redux_system.backLayout.isShrink,
          })}
        >
          <img className="w-6 opacity-70" src={ImgLogo} alt="logo" />
        </div>

        {redux_system.backLayout.isShrink ? (
          <TieredMenu className="w-full" model={menuItemTiered} />
        ) : (
          <PanelMenu
            className="w-full fadeinleft animation-ease-in animation-duration-200"
            model={menuItemPanel}
          />
        )}
      </div>
    </div>
  );
}

BackSidebar.propTypes = {
  LayoutCss: PropTypes.object.isRequired,
};
